import React, { useContext, useEffect, useState } from "react";
import moment from "moment";
import { useQuery } from "react-query";
import { createApiClient } from "apiClient";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  PatientContext,
  PatientContextState
} from "contextApis/patientContext";
import { CommonPatientExtensionItems } from "pages/patients/commonPatientExtensionItems";
import { RiskProfileData, RiskProfileList } from "./RiskProfileList";
import { RiskProfileTitleText } from "./styles";
import { Col, Empty, Row, Table } from "antd";
import { CardComponent } from "components/card/card";
import styled from "styled-components";
import { Colors } from "helpers/colors";
import axios from "axios";
import Tableau from "pages/dashboard/Tableau";
import { useParams } from "react-router";

type ContentContainerProps = {
  marginTop: string;
};

export const RiskProfilePage: React.FC = () => {
  const { id: patient_internal_id } = useParams();
  const [riskProfileData, setRiskProfileData] = useState<RiskProfileData[]>([]);
  const patientContext = useContext<PatientContextState>(PatientContext);
  const [tokenData, setTokenData] = useState<string>("");
  const [tokenError, setTokenError] = useState<null | string>(null);
  const [loading, setLoading] = useState(true);
  const { isLoading, data, error, isFetching } = useQuery(
    "risk-profile",
    async () => {
      return await createApiClient().getPatientRiskProfile(
        patientContext.patientData?.id || "",
        true
      );
    },
    { enabled: false }
  );

  const tableData = [
    {
      factor: "Z55  Problems related to education and literacy",
      risk: "Low",
      key: "1"
    },
    {
      factor: "Z56  Problems related to employment and unemployment",
      risk: "High",
      key: "2"
    },
    {
      factor: "Z57  Occupational exposure to risk factors",
      risk: "High",
      key: "3"
    },
    {
      factor: "Z58  Problems related to physical environment",
      risk: "High",
      key: "4"
    },
    {
      factor: "Z59  Problems related to housing and economic circumstance",
      risk: "Low",
      key: "5"
    },
    {
      factor: "Z60  Problems related to social environment",
      risk: "Low",
      key: "6"
    },
    { factor: "Z62  Problems related to upbringing", risk: "Low", key: "7" },
    {
      factor:
        "Z63  Other problems related to primary support group, including family circumstances",
      risk: "Low",
      key: "8"
    },
    {
      factor: "Z64  Problems related to certain psychosocial circumstances",
      risk: "Low",
      key: "9"
    },
    {
      factor: "Z65  Problems related to other psychosocial circumstances",
      risk: "Low",
      key: "10"
    }
  ];

  const chartOptions = {
    chart: {
      type: "pie",
      height: "100px",
      width: 100
    },
    title: {
      text: ""
    },
    plotOptions: {
      pie: {
        allowPointSelect: false,
        dataLabels: {
          enabled: false
        }
      }
    },
    series: [
      {
        name: "Data",
        data: [
          {
            x: "1-Jan-25",
            y: 0,
            color: "#808080"
          },
          {
            x: "1-Jan-24",
            y: 20,
            color: "#FFA500"
          },
          {
            x: "1-Jan-23",
            y: 40,
            color: "#FF0000"
          },
          {
            x: "1-Jan-22",
            y: 60,
            color: "#34973E"
          },
          {
            x: "1-Jan-21",
            y: 80,
            color: "#007FFF"
          }
        ]
      }
    ],
    credits: {
      enabled: false
    }
  };

  const lineChartOption = {
    chart: {
      type: "line",
      height: 190
    },
    plotOptions: {
      line: {
        allowPointSelect: false,
        dataLabels: {
          enabled: false
        }
      }
    },
    title: {
      text: `<div style="font-size: 13px; font-weight: bold; color: ${Colors.DodgerBlue}; letter-spacing: 1px;">
              Rate of GFR change – 15ml/min/year
            </div>`
    },
    xAxis: {
      categories: ["1-Jan-2021", "1-Jan-2022", "1-Jan-2023"],
      title: {
        text: ``
      }
    },
    yAxis: {
      reversed: true,
      title: {
        text: null
      },
      tickPositions: [80, 60, 40, 20, 0]
    },
    series: [
      {
        name: "EGFR",
        data: [65, 50, 29]
      }
    ],
    credits: {
      enabled: false
    }
  };

  const tableColumns = [
    {
      dataIndex: "factor",
      key: "factor",
      title: "Health Equity Factor"
    },
    {
      dataIndex: "risk",
      key: "risk",
      title: "Attributed Risk",
      width: "30%"
    }
  ];

  useEffect(() => {
    if (!data) return;
    const riskProfileData: RiskProfileData[] = data.map(profile => ({
      timestamp: moment(
        profile.create_date,
        "ddd, DD MMM YYYY hh:mm:ss z"
      ).format("MMM DD, YYYY | hh:mm a"),
      riskData: profile.risk_profile.split(/\n/).map(item => {
        const [key, value] = item.split(/:/);
        return {
          key: key,
          value: value.replace("#R", "").replace("#", "")
        };
      })
    }));
    setRiskProfileData(riskProfileData);
  }, [data]);

  useEffect(() => {
    const fetchToken = async () => {
      try {
        const config = {
          headers: {
            Authorization: sessionStorage.getItem("access_token") || ""
          }
        };
        const response = await axios.get(
          `${process.env["REACT_APP_API_ENDPOINT"] + "/users/tableau-token"}`,
          config
        );

        setTokenData(perv =>
          perv === response?.data?.data ? perv : response?.data?.data
        );
        setLoading(false);
      } catch (error: any) {
        setTokenError(error.message);
        setLoading(false);
      }
    };

    fetchToken();
  }, []);

  const options = {
    width: "1000px",
    height: "600px",
    onFirstInteractive: (e: any) => console.log("Viz loaded!", e)
  };

  const high_risk_patient = [
    "676",
    "1655",
    "6178",
    "2518",
    "4971",
    "1475",
    "983",
    "4536",
    "4392",
    "1000"
  ];

  const demoTableau = (patient_internal_id: any) => {
    if (patient_internal_id === "1744")
      return `https://prod-apnortheast-a.online.tableau.com/#/site/consinttableauvisualizations/views/RiskProfileDashboard710/RiskProfile?:iid=1`;
    if (patient_internal_id === "1745")
      return `https://prod-apnortheast-a.online.tableau.com/#/site/consinttableauvisualizations/views/RiskProfileDashboard700/RiskProfile?:iid=1`;
    if (patient_internal_id === "1746")
      return `https://prod-apnortheast-a.online.tableau.com/#/site/consinttableauvisualizations/views/RiskProfileDashboard563/RiskProfile?:iid=1`;
    if (patient_internal_id === "1747")
      return `https://prod-apnortheast-a.online.tableau.com/#/site/consinttableauvisualizations/views/RiskProfileDashboard756/RiskProfile?:iid=1`;
    if (patient_internal_id === "1748")
      return `https://prod-apnortheast-a.online.tableau.com/#/site/consinttableauvisualizations/views/RiskProfileDashboard396/RiskProfile?:iid=1`;
    if (patient_internal_id === "1726")
      return `https://prod-apnortheast-a.online.tableau.com/#/site/consinttableauvisualizations/views/RiskProfileDashboard396/RiskProfile?:iid=1`;
  };

  return (
    <>
      <CommonPatientExtensionItems />
      {/* <RiskProfileTitleText>Risk Profile</RiskProfileTitleText> */}

      {tokenData && patient_internal_id && (
        <Tableau
          url={demoTableau(patient_internal_id) || ""}
          options={options}
          token={tokenData}
        />
      )}
      {/* 
      {tokenData && patient_internal_id ? (
        <Tableau
          url={
            exlude_patient_list.includes(patient_internal_id)
              ? ""
              : high_risk_patient.includes(patient_internal_id)
              ? `https://prod-apnortheast-a.online.tableau.com/#/site/consinttableauvisualizations/views/RiskProfileforIKCHighRiskPatients${patient_internal_id}/RiskProfile?:iid=2`
              : `https://prod-apnortheast-a.online.tableau.com/#/site/consinttableauvisualizations/views/RiskProfileDashboard${patient_internal_id}/RiskProfile?:iid=1`
          }
          options={options}
          token={tokenData}
        />
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_DEFAULT}
          style={{ marginTop: "20vh" }}
        />
      )} */}
    </>
  );
};

const LabelTextComponent = styled.div`
  font-size: 15px;
  font-weight: bold;
  line-height: 17px;
  color: ${Colors.DodgerBlue};
  letter-spacing: 1px;
`;

const StyledContentContainer = styled.div`
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
`;

const ContentContainer = styled.div<ContentContainerProps>`
  align-self: end;
  margin-top: ${props => (props.marginTop ? props.marginTop : "")};
`;

const StyledText = styled.p`
  margin-bottom: 0px;
`;
